import React, { useState, useRef, useEffect } from 'react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const FoodScanner = ({ onClose }) => {
  const [useCamera, setUseCamera] = useState(false);
  const [capturedImage, setCapturedImage] = useState(null);
  const [analyzing, setAnalyzing] = useState(false);
  const [result, setResult] = useState(null);
  const [mealType, setMealType] = useState('snack');
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);

  const startCamera = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: 'environment' } });
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
      }
      setUseCamera(true);
    } catch (error) {
      alert('Failed to access camera: ' + error.message);
    }
  };

  const stopCamera = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }
    setUseCamera(false);
  };

  const capturePhoto = () => {
    const video = videoRef.current;
    const canvas = canvasRef.current;
    if (video && canvas) {
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      const ctx = canvas.getContext('2d');
      ctx.drawImage(video, 0, 0);
      const imageData = canvas.toDataURL('image/jpeg', 0.8);
      setCapturedImage(imageData);
      stopCamera();
    }
  };

  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (event) => {
        setCapturedImage(event.target.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const analyzeImage = async () => {
    if (!capturedImage) return;
    
    setAnalyzing(true);
    try {
      const base64Data = capturedImage.split(',')[1];
      const response = await axios.post(`${API}/meals/analyze`, {
        image_base64: base64Data,
        meal_type: mealType
      });
      setResult(response.data);
    } catch (error) {
      alert('Failed to analyze image: ' + error.message);
    } finally {
      setAnalyzing(false);
    }
  };

  const logMeal = async () => {
    if (!result) return;
    
    try {
      const base64Data = capturedImage.split(',')[1];
      await axios.post(`${API}/meals/log`, {
        food_name: result.food_name,
        calories: result.calories,
        protein: result.protein,
        carbs: result.carbs,
        fat: result.fat,
        image_base64: base64Data,
        meal_type: mealType
      });
      onClose();
    } catch (error) {
      alert('Failed to log meal: ' + error.message);
    }
  };

  useEffect(() => {
    return () => {
      stopCamera();
    };
  }, []);

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto p-6">
        <div className="flex justify-between items-center mb-4">
          <h2 className="heading-3" style={{ color: 'rgb(24, 24, 24)' }}>Scan Your Food</h2>
          <button onClick={onClose} className="text-2xl" style={{ color: 'rgb(112, 112, 112)' }}>×</button>
        </div>

        {!capturedImage && !useCamera && (
          <div className="space-y-4">
            <button onClick={startCamera} className="btn-primary w-full">Use Camera</button>
            <label className="btn-secondary w-full block text-center cursor-pointer">
              Upload Photo
              <input type="file" accept="image/*" onChange={handleFileUpload} className="hidden" />
            </label>
          </div>
        )}

        {useCamera && (
          <div className="space-y-4">
            <video
              ref={videoRef}
              autoPlay
              playsInline
              className="w-full rounded-lg"
            />
            <canvas ref={canvasRef} className="hidden" />
            <div className="flex gap-2">
              <button onClick={capturePhoto} className="btn-primary flex-1">Capture Photo</button>
              <button onClick={stopCamera} className="btn-secondary">Cancel</button>
            </div>
          </div>
        )}

        {capturedImage && !result && (
          <div className="space-y-4">
            <img src={capturedImage} alt="Captured food" className="w-full rounded-lg" />
            <div>
              <label className="body-medium block mb-2" style={{ color: 'rgb(24, 24, 24)' }}>Meal Type</label>
              <select
                value={mealType}
                onChange={(e) => setMealType(e.target.value)}
                className="w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                style={{ borderColor: 'rgba(24, 24, 24, 0.2)' }}
              >
                <option value="breakfast">Breakfast</option>
                <option value="lunch">Lunch</option>
                <option value="dinner">Dinner</option>
                <option value="snack">Snack</option>
              </select>
            </div>
            <div className="flex gap-2">
              <button onClick={analyzeImage} disabled={analyzing} className="btn-primary flex-1">
                {analyzing ? 'Analyzing...' : 'Analyze Food'}
              </button>
              <button onClick={() => setCapturedImage(null)} className="btn-secondary">Retake</button>
            </div>
          </div>
        )}

        {result && (
          <div className="space-y-4">
            <img src={capturedImage} alt="Analyzed food" className="w-full rounded-lg" />
            <div className="p-4 rounded-lg" style={{ backgroundColor: 'rgba(243, 234, 255, 0.3)' }}>
              <h3 className="heading-4 mb-3" style={{ color: 'rgb(24, 24, 24)' }}>{result.food_name}</h3>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <p className="body-small" style={{ color: 'rgb(112, 112, 112)' }}>Calories</p>
                  <p className="body-medium font-semibold" style={{ color: 'rgb(24, 24, 24)' }}>{Math.round(result.calories)}</p>
                </div>
                <div>
                  <p className="body-small" style={{ color: 'rgb(112, 112, 112)' }}>Protein</p>
                  <p className="body-medium font-semibold" style={{ color: 'rgb(24, 24, 24)' }}>{Math.round(result.protein)}g</p>
                </div>
                <div>
                  <p className="body-small" style={{ color: 'rgb(112, 112, 112)' }}>Carbs</p>
                  <p className="body-medium font-semibold" style={{ color: 'rgb(24, 24, 24)' }}>{Math.round(result.carbs)}g</p>
                </div>
                <div>
                  <p className="body-small" style={{ color: 'rgb(112, 112, 112)' }}>Fat</p>
                  <p className="body-medium font-semibold" style={{ color: 'rgb(24, 24, 24)' }}>{Math.round(result.fat)}g</p>
                </div>
              </div>
              {result.portion_size && (
                <p className="body-small mt-2" style={{ color: 'rgb(112, 112, 112)' }}>Portion: {result.portion_size}</p>
              )}
            </div>
            <div className="flex gap-2">
              <button onClick={logMeal} className="btn-primary flex-1">Log This Meal</button>
              <button onClick={() => { setCapturedImage(null); setResult(null); }} className="btn-secondary">Scan Again</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default FoodScanner;